/**
 * Parâmetros de mídia das evidências (fotos/PDF), compartilhados entre frontend
 * e backend para que os dois falem do MESMO alvo.
 *
 * Motivo de existir: as fotos de campo saem da câmera com 3–8 MB e iam cruas
 * para o bucket, estourando a banda de download do B2 (todo mundo que abre uma
 * avaliação baixa as fotos de novo). O cliente passa a redimensionar e
 * recomprimir ANTES de enfileirar/enviar — ver `frontend/src/lib/comprimirImagem.ts`.
 */

/** Maior dimensão (px) de uma foto de evidência após a compressão do cliente.
 *  2000 px preserva a leitura de texto em documento fotografado (~170 dpi numa
 *  folha A4) e ainda derruba o arquivo em ~90%. */
export const LARGURA_MAX_EVIDENCIA = 2000;

/** Qualidade do WebP (formato preferido: menor que JPEG na mesma percepção). */
export const QUALIDADE_WEBP_EVIDENCIA = 0.8;

/** Qualidade do JPEG — fallback para navegador sem encoder WebP no canvas. */
export const QUALIDADE_JPEG_EVIDENCIA = 0.82;

/** Alvo de tamanho por foto. Acima disto o compressor tenta de novo com menos
 *  qualidade (até `TENTATIVAS_COMPRESSAO`), sem nunca subir a resolução. */
export const ALVO_BYTES_EVIDENCIA = 600_000;

/** Quantas vezes reduzir a qualidade tentando alcançar `ALVO_BYTES_EVIDENCIA`. */
export const TENTATIVAS_COMPRESSAO = 3;

/**
 * Miniatura da galeria: objeto SEPARADO, gravado junto com a foto.
 *
 * Sem ela, exibir um grid de miniaturas de 60 px obrigava a baixar a foto
 * inteira de cada evidência — o maior multiplicador de banda que havia. 240 px
 * cobre com folga a maior célula da galeria (1/3 da largura no mobile, em telas
 * com `devicePixelRatio` 2/3) e sai em torno de 10 KB.
 */
export const LARGURA_MINIATURA = 240;
export const QUALIDADE_MINIATURA = 0.72;

/** Sufixo da chave da miniatura no bucket, derivado da chave do original. */
export const SUFIXO_MINIATURA = ".thumb.webp";

/** Quanto a qualidade cai a cada nova tentativa. */
export const PASSO_QUALIDADE = 0.07;

/**
 * `accept` dos inputs de arquivo.
 *
 * HEIC está FORA de propósito: quando o `accept` não menciona HEIC, o iOS
 * transcodifica a foto para JPEG sozinho, de graça. Declarar `image/heic`
 * (Safari 17+) produz o efeito INVERSO — o arquivo chega `.heic`, que o canvas
 * não decodifica fora do Safari e que o servidor não sabe processar.
 */
export const ACCEPT_IMAGEM = "image/jpeg,image/png,image/webp";
export const ACCEPT_IMAGEM_PDF = `${ACCEPT_IMAGEM},application/pdf`;

/** Teto por arquivo de evidência do checklist. Continua generoso de propósito:
 *  o cliente já comprime a foto antes de enviar (ver `comprimirImagem`), e este
 *  limite é rede de segurança — inclusive para PDFs digitalizados e para
 *  aparelhos ainda com a versão anterior do PWA em cache. */
export const LIMITE_BYTES_EVIDENCIA = 20_000_000;

/** Extensão → content-type, para quando o seletor de arquivos não informa o tipo. */
const TIPO_POR_EXTENSAO: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  pdf: "application/pdf",
};

/**
 * Content-type confiável do arquivo escolhido.
 *
 * Vários seletores de arquivo do Android entregam `File.type` VAZIO mesmo para
 * um JPEG comum. Sem isto a foto era enfileirada como `application/octet-stream`
 * — subia, mas a galeria deixava de reconhecê-la como imagem. Cair na extensão
 * do nome recupera o tipo real; `""` significa "não deu para saber".
 */
export function tipoDeEvidencia(nome: string, tipo: string): string {
  const informado = tipo.trim().toLowerCase();
  if (informado !== "") {
    return informado;
  }
  const extensao = nome.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? "";
  return TIPO_POR_EXTENSAO[extensao] ?? "";
}

/** O que o checklist aceita como evidência: qualquer imagem, ou PDF. */
export function aceitaComoEvidencia(contentType: string): boolean {
  const t = contentType.toLowerCase();
  return t.startsWith("image/") || t === "application/pdf";
}

/**
 * Motivo pelo qual este arquivo NÃO pode virar evidência, ou `null` se pode.
 *
 * Existe porque o `accept` do input é só uma sugestão — o seletor de arquivos do
 * Android costuma ignorá-lo — e porque o servidor recusa (400) o que passa do
 * limite. Barrar ANTES de enfileirar evita o pior caso: um arquivo impossível
 * parado na frente da fila offline, travando todas as evidências seguintes.
 */
export function motivoRecusaEvidencia(contentType: string, bytes: number): string | null {
  if (!aceitaComoEvidencia(contentType)) {
    return "Formato não aceito. Envie uma foto (JPG, PNG ou WebP) ou um PDF.";
  }
  if (bytes <= 0) {
    return "O arquivo está vazio. Tente capturar ou anexar de novo.";
  }
  if (bytes > LIMITE_BYTES_EVIDENCIA) {
    const mb = (bytes / 1_000_000).toFixed(1);
    const teto = Math.round(LIMITE_BYTES_EVIDENCIA / 1_000_000);
    return `Arquivo de ${mb} MB — o limite por evidência é ${teto} MB. Fotografe em resolução menor ou divida o PDF.`;
  }
  return null;
}
